import { create } from "zustand";

interface StoreActions<T> {
  setField: <K extends keyof T>(key: K, value: T[K] | ((prev: T[K]) => T[K])) => void;
  setPayload: (payload: Partial<T> | ((prev: T) => Partial<T>)) => void;
  reset: () => void;
}

type StoreState<T> = T & StoreActions<T>;

export const createStore = <T extends object>(initialState: T) => {
  const useStore = create<StoreState<T>>((set) => ({
    ...initialState,

    setField: (key, value) =>
      set((state) => ({
        ...state,
        [key]: typeof value === "function" ? (value as (prev: T[typeof key]) => T[typeof key])(state[key]) : value,
      })),

    setPayload: (payload) =>
      set((state) => ({
        ...state,
        ...(typeof payload === "function" ? payload(state as T) : payload),
      })),

    reset: () => set({ ...initialState } as StoreState<T>),
  }));

  const useState = (): T => {
    const { setField, setPayload, reset, ...state } = useStore() as StoreState<T>;
    return state as T;
  };

  const useActions = (): StoreActions<T> => {
    const { setField, setPayload, reset } = useStore();
    return { setField, setPayload, reset };
  };

  const getState = (): T => {
    const { setField, setPayload, reset, ...state } = useStore.getState();
    return state as T;
  };

  return { useStore, useState, useActions, getState };
};
